// src/components/chart-model-config.ts
//
// Chart-specific view of the model catalog.
//
// Every model name comes from the central catalog (`@/app/constants/models`).
// The only thing local to this file is the chart COLOR palette — the
// `chart-1 … chart-5` tokens, tuned for line/area legibility, which is
// intentionally different from the brand-accent colors in central used
// for badges and pills.
//
// The picker list `AVAILABLE_AI_MODELS` lives in `@/config/ai-models` —
// re-exported here only so older imports from `chart-model-config` keep
// resolving. New code should import directly from `@/config/ai-models`.

import {
  MODEL_CATALOG,
  type ProviderId,
  getModelDisplayName,
  getModelLabel,
} from "@/app/constants/models";

export interface ModelConfig {
  name: string;
  color: string;
}

// ─── Chart-specific color palette ─────────────────────────────────────────
// Maps central ProviderId → chart color. Edit here to retune the chart
// palette without touching the badge/accent colors in models.ts.
const CHART_COLOR_OVERRIDES: Record<ProviderId, string> = {
  openai:     "var(--chart-1)",
  // gptweb is fenced out of analytics charts server-side; the entry exists
  // only to satisfy the exhaustive ProviderId map.
  gptweb:     "var(--signal-blue)",
  claude:     "var(--chart-2)",
  perplexity: "var(--chart-3)",
  gemini:     "var(--chart-4)",
  // Google green, the same literal the catalog carries for this lane. Kept as a
  // literal rather than a chart token on purpose: no chart token is this hue,
  // and the lane is identified by Google green everywhere else in the product.
  // The map is typed Record<ProviderId, string>, so omitting it was a strict
  // typecheck error and an undefined color at runtime wherever MODEL_DISPLAY
  // feeds a chart series.
  aimode:     "#34A853",
  copilot:    "var(--chart-5)",
  llama:      "var(--chart-5)",
};

/** Known model keys → display config (derived from central catalog). */
export const MODEL_DISPLAY: Record<string, ModelConfig> = MODEL_CATALOG.reduce(
  (acc, m) => {
    acc[m.id] = {
      name: getModelDisplayName(m.id),
      color: CHART_COLOR_OVERRIDES[m.id],
    };
    return acc;
  },
  {} as Record<string, ModelConfig>,
);

/** Colors assigned to user-added or unknown models (cycled). */
export const EXTRA_COLORS: string[] = [
  "var(--solar-orange)",
  "var(--infrared-coral)",
  "var(--spectral-violet)",
  "var(--alpha-teal)",
  "var(--warning-text)",
  "var(--signal-blue)",
];

/**
 * Return display config for any model key.
 * Falls back to a generated name + extra color for unknown keys.
 *
 * @param key          Canonical model key (e.g. "openai", "claude")
 * @param allModelKeys Full ordered list of model keys currently in use,
 *                     used to assign a stable colour index to unknowns.
 */
export function getModelConfig(key: string, allModelKeys: string[]): ModelConfig {
  if (MODEL_DISPLAY[key]) return MODEL_DISPLAY[key];

  // Unknown model — pick a stable color from the extra palette based on
  // its position among the unknowns in allModelKeys.
  const knownCount = Object.keys(MODEL_DISPLAY).length;
  const extraIndex = allModelKeys.indexOf(key) - knownCount;
  const color      = EXTRA_COLORS[Math.max(0, extraIndex) % EXTRA_COLORS.length];

  // Use central helper for naming so anything aliased ("gpt", "anthropic")
  // still resolves to a nice label; otherwise generate from the raw key.
  const fromCatalog = getModelLabel(key, "");
  const name = fromCatalog
    || key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");

  return { name, color };
}

// Legacy flat export used by some older chart imports
export const modelConfig = MODEL_DISPLAY;

// ─── Picker re-exports (for backward compatibility) ───────────────────────
//
// The picker list now lives in `@/app/constants/models` alongside the
// catalog itself. These re-exports keep any older imports from
// `chart-model-config` resolving. New code should import these directly
// from `@/app/constants/models`.

export type { AIModel } from "@/app/constants/models";
export { AVAILABLE_AI_MODELS } from "@/app/constants/models";